import styled from 'styled-components'
import BigNumber from 'bignumber.js'
import { Box, Flex, Text, Heading, Skeleton, Card, Button } from '@pancakeswap/uikit'
import { useTranslation } from 'contexts/Localization'
import { useCapital, usePollBondsData, usePollBondsWithUserData } from 'state/capital/hooks'
import { BIG_TEN, BIG_ZERO } from 'utils/bigNumber'
import { StyledSwapContainer } from 'views/Swap/styles'
import { getBalanceNumber, getFullDisplayBalance } from 'utils/formatBalance'
import Page from 'components/Layout/Page'
import { useEffect, useState } from 'react'

const StepContainer = styled(Flex)`
  gap: 6px;
  width: 100%;
  flex-direction: column;
  ${({ theme }) => theme.mediaQueries.xl} {
    flex-direction: row;
    padding: 12px;
  }
`

const StyledStepCard = styled(Box)`
  display: flex;
  align-self: baseline;
  position: relative;
  background: ${({ theme }) => theme.colors.gradients};
  border-radius: 8px;
`

const StepCardInner = styled(Box)`
  width: 100%;
  padding: 6px;
  border-radius: 8px;
`

const StyledInputCurrencyWrapper = styled(Box)`
  width: 328px;
  text-align: -webkit-center;
  ${({ theme }) => theme.mediaQueries.sm} {
    width: 436px;
  }
  ${({ theme }) => theme.mediaQueries.md} {
    width: 800px;
  }
`

export const BodyWrapper = styled(Card)`
  border-radius: 12px;
  max-width: 800px;
  width: 100%;
  z-index: 1;
`

function AppBody({ children }: { children: React.ReactNode }) {
  return <BodyWrapper>{children}</BodyWrapper>
}

type Step = { title: string; subtitle: string; label: string }

const StepCard: React.FC<React.PropsWithChildren<{ step: Step }>> = ({ step }) => {
  return (
    <StyledStepCard width="100%">
      <StepCardInner>
        <Text mb="16px" fontSize="14px" bold textAlign="center">
          {step.label}
        </Text>
        {step.title === '' ? (
          <Skeleton width={100} height={27} mb={16} />
        ) : (
          <Heading scale="md" color="secondary">
            {step.title}
          </Heading>
        )}
        <Text color="textSubtle">{step.subtitle}</Text>
      </StepCardInner>
    </StyledStepCard>
  )
}

const Capital: React.FC = () => {
  const { bonds, vault } = useCapital()
  const [taiPrice, setTaiPrice] = useState('0')

  usePollBondsData()
  usePollBondsWithUserData()

  useEffect(() => {
    fetch(`https://api.dexscreener.com/latest/dex/pairs/solana/99a2qtflaxv9lvhkesjeawjky8pwb4cypc5ccucb4vus`).then(
      (res: any) => {
        if (res.ok) {
          res.json().then((json: any) => {
            setTaiPrice(json.pairs[0].priceUsd)
          })
        }
      },
    )
  }, [])

  const treasuryBalances = bonds ? bonds.map((bond) => bond.purchased) : []
  let totalPurchased = treasuryBalances.reduce(
    (value0, value1, index) =>
      new BigNumber(value0).plus(new BigNumber(value1).div(BIG_TEN.pow(bonds[index].bondToken.decimals))),
    BIG_ZERO,
  )

  const { t } = useTranslation()

  const tasTge = Number(taiPrice) * 69230
  totalPurchased = totalPurchased.plus(new BigNumber(12500)).plus(new BigNumber(8333)).plus(new BigNumber(tasTge))
  const steps1: Step[] = [
    {
      label: t('VNTR Price'),
      title: vault
        ? `$${vault.marketPrice ? getBalanceNumber(new BigNumber(vault.marketPrice), 0).toLocaleString() : '-'}`
        : '',
      subtitle: t(''),
    },
    {
      label: t('MarketCap'),
      title: vault
        ? `$${vault.marketCap ? getFullDisplayBalance(new BigNumber(vault.marketCap), 0, 0).toLocaleString() : '-'}`
        : '',
      subtitle: t(''),
    },
    {
      label: t('Circulating Supply(Total)'),
      title: vault
        ? `${vault.circSupply ? getFullDisplayBalance(new BigNumber(vault?.circSupply), 9, 0) : '-'}/${
            vault.totalSupply ? getFullDisplayBalance(new BigNumber(vault?.totalSupply), 9, 0) : '-'
          }`
        : '',
      subtitle: t(''),
    },
  ]

  const steps2: Step[] = [
    {
      label: t('Backing per VNTR'),
      title: `$${
        !totalPurchased.isNaN() && vault?.circSupply
          ? getFullDisplayBalance(
              new BigNumber(totalPurchased).times(BIG_TEN.pow(9)).div(new BigNumber(vault?.circSupply)),
              0,
              3,
            )
          : '-'
      }`,
      subtitle: t(''),
    },
    {
      label: t('Current Index'),
      title: vault
        ? `${vault.currentIndex ? getFullDisplayBalance(new BigNumber(vault.currentIndex), 9, 2) : '-'} VNTR`
        : '',
      subtitle: t(''),
    },
    {
      label: t('Treasury Balance'),
      title: `$${!totalPurchased.isNaN() ? getFullDisplayBalance(new BigNumber(totalPurchased), 0, 0) : '-'}`,
      subtitle: t(''),
    },
  ]
  return (
    <Page>
      <Flex width="100%" justifyContent="center" position="relative" mb={4}>
        <StyledSwapContainer $isChartExpanded={false}>
          <StyledInputCurrencyWrapper>
            <AppBody>
              <Box width="100%" p={24}>
                <Flex justifyContent="center">
                  <StepContainer>
                    {steps1.map((step) => (
                      <StepCard key={step.label} step={step} />
                    ))}
                  </StepContainer>
                </Flex>
                <Flex justifyContent="center">
                  <StepContainer>
                    {steps2.map((step) => (
                      <StepCard key={step.label} step={step} />
                    ))}
                  </StepContainer>
                </Flex>
              </Box>
            </AppBody>
          </StyledInputCurrencyWrapper>
        </StyledSwapContainer>
      </Flex>
      <Flex width="100%" justifyContent="center" position="relative" mb={4}>
        <StyledSwapContainer $isChartExpanded={false}>
          <StyledInputCurrencyWrapper>
            <AppBody>
              <Box width="100%" p={24}>
                <Text mb="16px" fontSize="20px" bold textAlign="center">
                  Crypto Holdings
                </Text>
                <Flex justifyContent="center" flexDirection="column">
                  <Flex justifyContent="space-between" mb={2}>
                    <Text width="30%">Token</Text>
                    <Text width="30%">QTY</Text>
                    <Text width="30%">USD Value</Text>
                  </Flex>
                  {bonds.map((bond) => {
                    return (
                      <Flex justifyContent="space-between">
                        <Text width="30%" color="primary">
                          {bond.bondToken.symbol}
                        </Text>
                        <Text width="30%" color="primary">
                          {bond?.treasuryBalance ? (
                            `${getFullDisplayBalance(new BigNumber(bond?.treasuryBalance), bond.bondToken.decimals, 4)}`
                          ) : (
                            <Skeleton width={100} height={24} mb={1} />
                          )}
                        </Text>
                        <Text width="30%" color="primary">
                          {bond?.purchased ? (
                            `$${getFullDisplayBalance(new BigNumber(bond.purchased), bond.bondToken.decimals, 0)}`
                          ) : (
                            <Skeleton width={100} height={24} mb={1} />
                          )}
                        </Text>
                      </Flex>
                    )
                  })}
                </Flex>
              </Box>
            </AppBody>
          </StyledInputCurrencyWrapper>
        </StyledSwapContainer>
      </Flex>
      <Flex width="100%" justifyContent="center" position="relative">
        <StyledSwapContainer $isChartExpanded={false}>
          <StyledInputCurrencyWrapper>
            <AppBody>
              <Box width="100%" p={24}>
                <Text mb="16px" fontSize="20px" bold textAlign="center">
                  Early Stage Investment
                </Text>
                <Flex justifyContent="center" flexDirection="column">
                  <Flex justifyContent="space-between" mb={2}>
                    <Text width="33%" style={{ overflowWrap: 'anywhere' }}>
                      Project/Company
                    </Text>
                    <Text width="33%" style={{ overflowWrap: 'anywhere' }}>
                      Asset QTY
                    </Text>
                    <Text width="33%" style={{ overflowWrap: 'anywhere' }}>
                      TGE Value
                    </Text>
                  </Flex>
                  <Flex justifyContent="space-between">
                    <Text width="33%">
                      <Button as="a" href="https://tars.pro/" target="_blank" scale="sm" variant="text">
                        TARS(TAI)
                      </Button>
                    </Text>
                    <Text width="33%" color="primary">
                      69,230
                    </Text>
                    <Text width="33%" color="primary">
                      ${tasTge.toLocaleString()}
                    </Text>
                  </Flex>
                  <Flex justifyContent="space-between">
                    <Text width="33%">
                      <Button as="a" href="https://solvent.app/" target="_blank" scale="sm" variant="text">
                        SOLVENT
                      </Button>
                    </Text>
                    <Text width="33%" color="primary">
                      50,000
                    </Text>
                    <Text width="33%" color="primary">
                      $12,500
                    </Text>
                  </Flex>
                  <Flex justifyContent="space-between">
                    <Text width="33%">
                      <Button as="a" href="https://projectvea.com/" target="_blank" scale="sm" variant="text">
                        VEA
                      </Button>
                    </Text>
                    <Text width="33%" color="primary">
                      16,666
                    </Text>
                    <Text width="33%" color="primary">
                      $8,333
                    </Text>
                  </Flex>
                </Flex>
              </Box>
            </AppBody>
          </StyledInputCurrencyWrapper>
        </StyledSwapContainer>
      </Flex>
    </Page>
  )
}

export default Capital
